import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useAuth, SignedIn, SignedOut, SignInButton } from '../lib/auth';
import { useState, useEffect } from 'react';
import { useKitchen } from '../hooks/useKitchen';

export const Route = createFileRoute('/join/$token')({
  component: JoinKitchenPage,
});

function JoinKitchenPage() {
  const { token } = Route.useParams();
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'joining' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [kitchenName, setKitchenName] = useState<string | null>(null);

  const { joinKitchen } = useKitchen();
  
  const joinMutation = useMutation({
    mutationFn: async () => {
      return joinKitchen(token);
    },
    onSuccess: () => {
      setStatus('success');
      setKitchenName('the kitchen');
      // Redirect to kitchen after a moment
      setTimeout(() => {
        navigate({ to: '/app' });
      }, 2000);
    },
    onError: (err: Error) => {
      setStatus('error');
      setError(err.message);
    },
  });

  // Auto-join if signed in
  useEffect(() => {
    if (isLoaded && isSignedIn && status === 'idle') {
      setStatus('joining');
      joinMutation.mutate();
    }
  }, [isLoaded, isSignedIn, status]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-snow flex items-center justify-center">
        <div className="text-stone">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-snow flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <span className="text-5xl mb-4 block">🍽️</span>
          <h1 className="font-display text-2xl font-semibold text-ink">
            Join Kitchen
          </h1>
        </div>

        <div className="bg-white rounded-2xl border border-mist p-6 shadow-sm">
          <SignedOut>
            <div className="text-center">
              <p className="text-stone mb-6">
                You've been invited to share a kitchen on MiseBook! Sign in or create an account to join.
              </p>
              <SignInButton mode="modal">
                <button className="btn btn-primary btn-lg w-full">
                  Sign in to Join
                </button>
              </SignInButton>
            </div>
          </SignedOut>

          <SignedIn>
            {status === 'joining' && (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-coral-200 border-t-coral-500 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-stone">Joining kitchen...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✓</span>
                </div>
                <h2 className="font-display text-xl font-semibold text-ink mb-2">
                  Welcome to {kitchenName}!
                </h2>
                <p className="text-stone">
                  Redirecting to your kitchen...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✕</span>
                </div>
                <h2 className="font-display text-xl font-semibold text-ink mb-2">
                  Couldn't Join
                </h2>
                <p className="text-rose-600 mb-4">
                  {error || 'This invite link may be invalid or expired.'}
                </p>
                <button
                  onClick={() => navigate({ to: '/app' })}
                  className="btn btn-secondary"
                >
                  Go to App
                </button>
              </div>
            )}
          </SignedIn>
        </div>
      </div>
    </div>
  );
}
